// Regex that matches all words which are properly defined variable names
// (Case in-sensitive, alphanumeric, optionally split by "_", or ".")
const IS_VARIABLE_REGEX = /^\$[a-zA-Z]+[a-zA-Z0-9]*(?:[_.][a-zA-Z0-9]+)*$/g;

export const BASE_RESERVED_CHARACTERS = ' *,\\()!<>="$:[\\]~';
export const RESERVED_CHARACTERS = `${BASE_RESERVED_CHARACTERS}\\\\`;

/**
 * Regex that matches all characters that need to be escaped, but are not.
 * The backslash needs to be excluded in the BASE_RESERVED_CHARACTERS to not get
 * overlapping matches.
 * E.g. foo = \*
 * Should not match, but would if we included the backslash in the first character set.
 * Regex + Javascript string escaping = Fun with backslashes.
 * https://regex101.com/r/gPeWnE
 * @internal
 */
export const NEEDS_ESCAPING_REGEX = `(?<!\\\\)(?:\\\\\\\\)*[${BASE_RESERVED_CHARACTERS}]|(\\\\$)`;

/**
 * Regex that matches all escaped characters, including the escape character and escaped character.
 * https://regex101.com/r/LNo0qJ/1
 * @internal
 */
export const ESCAPED_CHARACTERS_REGEX = /\\(?:.)/g;

/**
 * Escapes a suggestion value by wrapping it in double quotes if it contains
 * reserved characters that need escaping.
 *
 * @param value - The suggestion value to escape.
 * @returns The escaped suggestion value, wrapped in double quotes if necessary.
 * @public
 */
export function escapeSuggestion(value: string): string {
  // A standalone asterisk (position operator) and exists operators are the
  // only suggestions that should not be escaped automatically.
  if (value === '*' || value === '= *' || value === '!= *') {
    return value;
  }

  // Properly defined variables should not get escaped
  if (value.startsWith('$') && new RegExp(IS_VARIABLE_REGEX).exec(value)) {
    return value;
  }

  // Regex to check if the string is already escaped (also checking for escaped double quotes inside the string)
  // https://regex101.com/r/8SXgGW/1
  const isSuggestionEscaped = new RegExp(/^"(?:[^"\\]|\\")*"$/).exec(value);

  if (isSuggestionEscaped) {
    return value;
  }

  const matches = new RegExp(NEEDS_ESCAPING_REGEX, 'g').exec(value);

  // If the regex matches, there is at least one character in the applied suggestion that
  // needs to be escaped, but is not. In this case, we wrap the whole suggestion in
  // doublequotes. Doublequotes occurring in the applied value that are not yet escaped are
  // escaped with a backslash to not close the string. A single trailing backslash (\) is also
  // escaped to avoid ending the string with an unescaped backslash. It would not be necessary to
  // wrap 'te\"st' in doublequotes, since the included doublequote is already escaped by a
  // backslash. To not overcomplicate the logic, we still wrap the whole value.
  if (matches) {
    return `"${value
      .replace(/(?<!\\)(?:\\\\)*"/g, '\\"')
      .replace(/(?<!\\)(?:\\\\)*\*/g, '\\*')
      .replace(/(?<!\\)(?:\\\\)*\\$/g, '$&\\')}"`;
  }

  return value;
}
